import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService.js';

// Storage keys for localStorage
const STORAGE_KEYS = {
  CHAT_SESSIONS: 'trestle_chat_sessions',
  ACTIVE_SESSION: 'trestle_active_session',
};

// Storage utilities
const StorageUtils = {
  // Save sessions to localStorage
  saveSessions: (sessions) => {
    try {
      const sessionData = Array.from(sessions.entries()).map(([id, session]) => [
        id,
        {
          ...session,
          // Convert dates to strings for JSON serialization
          lastActivity: session.lastActivity,
          messages: session.messages.map((msg) => ({
            ...msg,
            timestamp: msg.timestamp,
          })),
        },
      ]);
      localStorage.setItem(STORAGE_KEYS.CHAT_SESSIONS, JSON.stringify(sessionData));
    } catch (error) {
      console.warn('Failed to save sessions to localStorage:', error);
    }
  },

  // Load sessions from localStorage
  loadSessions: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CHAT_SESSIONS);
      if (stored) {
        const sessionData = JSON.parse(stored);
        const sessions = new Map(sessionData);
        console.log(`Loaded ${sessions.size} chat sessions from localStorage`);
        return sessions;
      }
    } catch (error) {
      console.warn('Failed to load sessions from localStorage:', error);
    }
    return new Map();
  },

  // Save active session ID
  saveActiveSession: (sessionId) => {
    try {
      if (sessionId) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, sessionId);
      } else {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_SESSION);
      }
    } catch (error) {
      console.warn('Failed to save active session to localStorage:', error);
    }
  },

  // Load active session ID
  loadActiveSession: () => {
    try {
      return localStorage.getItem(STORAGE_KEYS.ACTIVE_SESSION);
    } catch (error) {
      console.warn('Failed to load active session from localStorage:', error);
      return null;
    }
  },

  // Clear all stored data
  clearStorage: () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.CHAT_SESSIONS);
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_SESSION);
      console.log('Cleared chat data from localStorage');
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  },

  // Check storage size and cleanup if needed
  cleanupIfNeeded: (sessions) => {
    try {
      const totalSize = new Blob([JSON.stringify(Array.from(sessions.entries()))]).size;
      const maxSize = 4 * 1024 * 1024; // 4MB limit (localStorage is usually 5-10MB)

      if (totalSize > maxSize) {
        console.log(`Storage size ${totalSize} exceeds limit, cleaning up old sessions`);

        // Convert to array and sort by last activity (oldest first)
        const sortedSessions = Array.from(sessions.entries()).sort((a, b) => {
          return new Date(a[1].lastActivity) - new Date(b[1].lastActivity);
        });

        // Remove oldest sessions until we're under the limit
        while (sortedSessions.length > 0) {
          const [oldestId] = sortedSessions.shift();
          sessions.delete(oldestId);
          console.log(`Removed old session: ${oldestId}`);

          const newSize = new Blob([JSON.stringify(Array.from(sessions.entries()))]).size;
          if (newSize < maxSize * 0.8) {
            // Leave some buffer
            break;
          }
        }

        console.log(`Cleanup complete. ${sessions.size} sessions remaining`);
        return true; // Indicate cleanup occurred
      }
    } catch (error) {
      console.warn('Failed to cleanup storage:', error);
    }
    return false;
  },
};

// useMultiChat Hook for Multi-Agent Chat State Management
export const useMultiChat = (authToken) => {
  const [chatSessions, setChatSessions] = useState(() => StorageUtils.loadSessions());
  const [activeSessionId, setActiveSessionId] = useState(() => StorageUtils.loadActiveSession());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Note: No longer auto-initializing with default session
  // Users will start with empty state and manually create first session

  // Auto-save sessions to localStorage whenever they change
  useEffect(() => {
    // Always save sessions, even if empty (to clear localStorage when all sessions are deleted)
    if (chatSessions.size > 0) {
      // Check if cleanup is needed before saving
      const cleanedSessions = new Map(chatSessions);
      const wasCleanedUp = StorageUtils.cleanupIfNeeded(cleanedSessions);

      if (wasCleanedUp) {
        // If we cleaned up, update the state and save the cleaned version
        setChatSessions(cleanedSessions);
        StorageUtils.saveSessions(cleanedSessions);
      } else {
        // Normal save
        StorageUtils.saveSessions(chatSessions);
      }
    } else {
      // Explicitly clear localStorage when there are no sessions
      try {
        localStorage.removeItem(STORAGE_KEYS.CHAT_SESSIONS);
        console.log('Cleared chat sessions from localStorage (no sessions remaining)');
      } catch (error) {
        console.warn('Failed to clear sessions from localStorage:', error);
      }
    }
  }, [chatSessions]);

  // Auto-save active session whenever it changes
  useEffect(() => {
    StorageUtils.saveActiveSession(activeSessionId);
  }, [activeSessionId]);

  // Validation effect - ensure active session exists in sessions
  useEffect(() => {
    if (activeSessionId && !chatSessions.has(activeSessionId)) {
      console.warn(`Active session ${activeSessionId} not found in sessions, clearing active session`);
      setActiveSessionId(null);
    }
  }, [activeSessionId, chatSessions]);

  // Start a new chat session
  const startNewSession = useCallback(
    async (sessionId, sessionName, agentOrId = null) => {
      if (!authToken) {
        setError('Authentication required');
        return { success: false, error: 'Authentication required' };
      }

      try {
        setIsLoading(true);
        setError('');

        // Handle both agent object and agentId string
        let agentId = null;
        let agent = null;

        if (typeof agentOrId === 'string') {
          agentId = agentOrId;
        } else if (agentOrId && typeof agentOrId === 'object') {
          agent = agentOrId;
          agentId = agent.id;
        }

        if (agentId !== null && agentId !== undefined && !Number.isInteger(Number(agentId))) {
          const errorMsg = 'Agent ID must be an integer';
          console.error(`startNewSession: ${errorMsg}`, agentId);
          return { success: false, error: errorMsg };
        }

        const sessionData = {
          ...(sessionId ? { sessionId } : {}),
          agentId: agentId,
          personality: agent?.configuration?.personality || 'helpful',
        };

        const result = await apiService.startChatSession(authToken, sessionData);

        if (result.ok) {
          // Generate welcome message based on agent configuration
          let welcomeMessage = `# Welcome to ${sessionName}`;

          // Use agent-specific welcome message if available
          if (agent?.configuration?.welcome_message) {
            welcomeMessage = agent.configuration.welcome_message;
          }

          const newSession = {
            id: result.data.sessionId,
            name: sessionName || 'New Chat',
            agentId: agentId,
            messages: [
              {
                id: 'welcome',
                type: 'system',
                content: welcomeMessage,
                timestamp: new Date().toISOString(),
              },
            ],
            isActive: true,
            lastActivity: new Date().toISOString(),
          };

          setChatSessions((prev) => {
            const updated = new Map(prev);
            updated.set(newSession.id, newSession);
            return updated;
          });

          setActiveSessionId(newSession.id);
          setSuccess('Chat session started successfully!');

          // If agent has an initial prompt, send it automatically
          if (agent?.configuration?.initial_prompt) {
            // Small delay to ensure session is fully initialized
            setTimeout(async () => {
              try {
                const initialPrompt = agent.configuration.initial_prompt;
                console.log(`Sending initial prompt for agent ${agentId}: "${initialPrompt}"`);

                // Use the sendMessage function to send the initial prompt
                const messageResult = await apiService.sendMessage(authToken, newSession.id, initialPrompt, {
                  agentId: agentId,
                });

                if (messageResult.ok) {
                  const botMessage = {
                    id: `bot-${Date.now()}`,
                    type: 'bot',
                    content: messageResult.data.text,
                    timestamp: new Date().toISOString(),
                    usage: messageResult.data.usage,
                    // Include grounding metadata for RAG-enhanced responses
                    groundingMetadata: messageResult.data.groundingMetadata || null,
                  };

                  // Add bot response to session
                  setChatSessions((prev) => {
                    const updated = new Map(prev);
                    const session = updated.get(newSession.id);
                    if (session) {
                      // Add user message for the initial prompt first
                      const userMessage = {
                        id: `msg-${Date.now()}-initial`,
                        type: 'user',
                        content: initialPrompt,
                        timestamp: new Date().toISOString(),
                      };
                      session.messages = [...session.messages, userMessage, botMessage];
                      session.lastActivity = new Date().toISOString();
                      updated.set(newSession.id, session);
                    }
                    return updated;
                  });
                }
              } catch (error) {
                console.error('Failed to send initial prompt:', error);
              }
            }, 500); // 500ms delay to ensure session is ready
          }

          return { success: true, sessionId: newSession.id };
        } else {
          const errorMsg = result.error?.message || 'Failed to start chat session';
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }
      } catch (err) {
        const errorMsg = 'Network error starting chat session';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setIsLoading(false);
      }
    },
    [authToken],
  );

  // Switch to a different chat session
  const switchSession = useCallback(
    (sessionId) => {
      if (chatSessions.has(sessionId)) {
        setActiveSessionId(sessionId);

        // Update last activity for the session being switched to
        setChatSessions((prev) => {
          const updated = new Map(prev);
          const session = updated.get(sessionId);
          if (session) {
            session.lastActivity = new Date().toISOString();
            updated.set(sessionId, session);
          }
          return updated;
        });

        return { success: true };
      } else {
        setError('Session not found');
        return { success: false, error: 'Session not found' };
      }
    },
    [chatSessions],
  );

  // Send message to active session
  const sendMessage = useCallback(
    async (messageText, attachments = []) => {
      if ((!messageText || !messageText.trim()) && attachments.length === 0) {
        return { success: false, error: 'Invalid message or session' };
      }
      if (!activeSessionId || !authToken) {
        return { success: false, error: 'Invalid message or session' };
      }

      const session = chatSessions.get(activeSessionId);
      if (!session) {
        return { success: false, error: 'Active session not found' };
      }

      const userMessage = {
        id: `msg-${Date.now()}`,
        type: 'user',
        content: messageText || '',
        timestamp: new Date().toISOString(),
        attachments:
          attachments.length > 0 ? attachments.map((a) => ({ name: a.name, type: a.type, size: a.size })) : undefined,
      };

      setChatSessions((prev) => {
        const updated = new Map(prev);
        const session = updated.get(activeSessionId);
        if (session) {
          session.messages = [...session.messages, userMessage];
          session.lastActivity = new Date().toISOString();
          updated.set(activeSessionId, session);
        }
        return updated;
      });

      setIsLoading(true);
      setError('');

      try {
        const session = chatSessions.get(activeSessionId);
        const result = await apiService.sendMessage(authToken, activeSessionId, messageText || '', {
          agentId: session?.agentId,
          ...(attachments.length > 0 ? { attachments } : {}),
        });

        if (result.ok) {
          const botMessage = {
            id: `bot-${Date.now()}`,
            type: 'bot',
            content: result.data.text,
            timestamp: new Date().toISOString(),
            usage: result.data.usage,
            // Include grounding metadata for RAG-enhanced responses
            groundingMetadata: result.data.groundingMetadata || null,
          };

          // Add bot message to session
          setChatSessions((prev) => {
            const updated = new Map(prev);
            const session = updated.get(activeSessionId);
            if (session) {
              session.messages = [...session.messages, botMessage];
              session.lastActivity = new Date().toISOString();
              updated.set(activeSessionId, session);
            }
            return updated;
          });

          return { success: true, message: result.data.text };
        } else {
          const errorMsg = result.error?.message || 'Failed to send message';
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }
      } catch (err) {
        const errorMsg = 'Network error sending message';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setIsLoading(false);
      }
    },
    [activeSessionId, authToken, chatSessions],
  );

  // Send message with streaming response
  const sendMessageStream = useCallback(
    async (messageText, attachments = []) => {
      if ((!messageText || !messageText.trim()) && attachments.length === 0) {
        return { success: false, error: 'Invalid message or session' };
      }
      if (!activeSessionId || !authToken) {
        return { success: false, error: 'Invalid message or session' };
      }

      const session = chatSessions.get(activeSessionId);
      if (!session) {
        return { success: false, error: 'Active session not found' };
      }

      const userMessage = {
        id: `msg-${Date.now()}`,
        type: 'user',
        content: messageText || '',
        timestamp: new Date().toISOString(),
        attachments:
          attachments.length > 0 ? attachments.map((a) => ({ name: a.name, type: a.type, size: a.size })) : undefined,
      };

      setChatSessions((prev) => {
        const updated = new Map(prev);
        const session = updated.get(activeSessionId);
        if (session) {
          session.messages = [...session.messages, userMessage];
          session.lastActivity = new Date().toISOString();
          updated.set(activeSessionId, session);
        }
        return updated;
      });

      // Create placeholder message for streaming content
      const botMessageId = `bot-${Date.now()}`;
      const initialBotMessage = {
        id: botMessageId,
        type: 'bot',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true,
        activitySteps: [
          {
            id: `step-thinking-${Date.now()}`,
            type: 'thinking',
            label: 'Analyzing your message...',
            detail: null,
            status: 'active',
            timestamp: new Date().toISOString(),
          },
        ],
      };

      // Add initial bot message
      setChatSessions((prev) => {
        const updated = new Map(prev);
        const session = updated.get(activeSessionId);
        if (session) {
          session.messages = [...session.messages, initialBotMessage];
          session.lastActivity = new Date().toISOString();
          updated.set(activeSessionId, session);
        }
        return updated;
      });

      setIsLoading(true);
      setError('');

      try {
        const session = chatSessions.get(activeSessionId);

        // Handle streaming chunks
        const onChunk = (chunk) => {
          setChatSessions((prev) => {
            const updated = new Map(prev);
            const session = updated.get(activeSessionId);
            if (session) {
              const messageIndex = session.messages.findIndex((msg) => msg.id === botMessageId);
              if (messageIndex !== -1) {
                const updatedMessages = [...session.messages];
                const currentMessage = updatedMessages[messageIndex];

                // Helper to build updated activity steps
                const prevSteps = currentMessage.activitySteps || [];
                let nextSteps = prevSteps;

                // Handle different event types
                if (chunk.type === 'function_calls') {
                  // Mark thinking step as completed, add function_call steps
                  const newSteps = prevSteps.map((s) =>
                    s.type === 'thinking' && s.status === 'active' ? { ...s, status: 'completed' } : s,
                  );
                  const callSteps = (chunk.functionCalls || []).map((fc, i) => {
                    const isA2A = fc.name && (fc.name.startsWith('a2a_session_') || fc.name.startsWith('a2a_'));
                    const targetAgent = fc.args?.targetAgentName || fc.args?.target_agent_name || null;
                    let label = `Calling ${fc.name}`;
                    if (isA2A && targetAgent) {
                      label = `Talking to ${targetAgent}`;
                    } else if (isA2A) {
                      label = 'Communicating with another agent';
                    }
                    return {
                      id: `step-fc-${Date.now()}-${i}`,
                      type: isA2A ? 'a2a_call' : 'function_call',
                      label,
                      detail: fc.args ? JSON.stringify(fc.args, null, 2) : null,
                      status: 'active',
                      timestamp: new Date().toISOString(),
                    };
                  });
                  nextSteps = [...newSteps, ...callSteps];

                  updatedMessages[messageIndex] = {
                    ...currentMessage,
                    functionCalls: chunk.functionCalls || [],
                    activitySteps: nextSteps,
                    isStreaming: true,
                  };
                } else if (chunk.type === 'function_result') {
                  // Mark matching function_call step as completed
                  const newSteps = prevSteps.map((s) =>
                    s.type === 'function_call' && s.status === 'active' && s.label === `Calling ${chunk.functionName}`
                      ? { ...s, status: 'completed', label: `Called ${chunk.functionName}` }
                      : s,
                  );
                  nextSteps = newSteps;

                  const functionCalls = currentMessage.functionCalls || [];
                  const updatedFunctionCalls = functionCalls.map((fc) =>
                    fc.name === chunk.functionName ? { ...fc, result: chunk.functionResult, status: 'completed' } : fc,
                  );
                  updatedMessages[messageIndex] = {
                    ...currentMessage,
                    functionCalls: updatedFunctionCalls,
                    activitySteps: nextSteps,
                    isStreaming: true,
                  };
                } else if (chunk.type === 'chunk' && chunk.chunk) {
                  const hasActiveNonTextSteps = prevSteps.some((s) => s.status === 'active');
                  if (hasActiveNonTextSteps) {
                    nextSteps = prevSteps.map((s) => (s.status === 'active' ? { ...s, status: 'completed' } : s));
                  }

                  const newContent = (currentMessage.content || '') + chunk.chunk;
                  const isStructuredJson =
                    newContent.trim().startsWith('```json') ||
                    (newContent.includes('"type":') && newContent.includes('"components"')) ||
                    (newContent.includes('"display_table"') && newContent.includes('"table_data"'));

                  updatedMessages[messageIndex] = {
                    ...currentMessage,
                    content: isStructuredJson ? '' : newContent,
                    functionCalls: chunk.functionCalls || currentMessage.functionCalls || [],
                    activitySteps: nextSteps,
                    isStreaming: true,
                    expectingStructured: isStructuredJson,
                  };
                } else if (chunk.type === 'content' && chunk.content) {
                  nextSteps = prevSteps.map((s) => (s.status === 'active' ? { ...s, status: 'completed' } : s));
                  updatedMessages[messageIndex] = {
                    ...currentMessage,
                    content: chunk.content,
                    functionCalls: chunk.functionCalls || currentMessage.functionCalls || [],
                    activitySteps: nextSteps,
                    isStreaming: true,
                  };
                } else if (chunk.type === 'status') {
                  // Add a status activity step
                  const statusStep = {
                    id: `step-status-${Date.now()}`,
                    type: 'status',
                    label: chunk.message || chunk.status || 'Processing...',
                    detail: null,
                    status: 'active',
                    timestamp: new Date().toISOString(),
                  };
                  // Mark previous status steps as completed
                  const newSteps = prevSteps.map((s) =>
                    s.type === 'status' && s.status === 'active' ? { ...s, status: 'completed' } : s,
                  );
                  nextSteps = [...newSteps, statusStep];

                  updatedMessages[messageIndex] = {
                    ...currentMessage,
                    activitySteps: nextSteps,
                    isStreaming: true,
                    statusMessage: chunk.message || chunk.status,
                  };
                }

                session.messages = updatedMessages;
                session.lastActivity = new Date().toISOString();
                updated.set(activeSessionId, session);
              }
            }
            return updated;
          });
        };

        // Handle completion
        const onComplete = (result) => {
          setChatSessions((prev) => {
            const updated = new Map(prev);
            const session = updated.get(activeSessionId);
            if (session) {
              const messageIndex = session.messages.findIndex((msg) => msg.id === botMessageId);
              if (messageIndex !== -1) {
                const updatedMessages = [...session.messages];
                const currentMessage = updatedMessages[messageIndex];

                // Determine content to set
                let content = null; // Use null to indicate "no new content"

                if (result.structured && result.data) {
                  content = result.data;
                } else if (result.fullText) {
                  content = result.fullText;
                } else if (result.text) {
                  // Also check for 'text' property as fallback
                  content = result.text;
                }

                // If no new content available, preserve existing content
                // This handles the case where a second "complete" event is sent without fullText
                if (content === null) {
                  content = currentMessage.content;
                }

                // Preserve activity steps and mark any remaining active ones as completed
                const finalSteps = (currentMessage.activitySteps || []).map((s) =>
                  s.status === 'active' ? { ...s, status: 'completed' } : s,
                );

                updatedMessages[messageIndex] = {
                  ...currentMessage,
                  content: content,
                  isStreaming: false,
                  expectingStructured: false,
                  activitySteps: finalSteps,
                  groundingMetadata: result.groundingMetadata || currentMessage.groundingMetadata || null,
                };
                session.messages = updatedMessages;
                session.lastActivity = new Date().toISOString();
                updated.set(activeSessionId, session);
              }
            }
            return updated;
          });
          setIsLoading(false);
        };

        // Handle errors
        const onError = (error) => {
          console.error('Hook: Streaming error:', error);
          const errorMsg = error.error || 'Error during streaming';
          setError(errorMsg);

          // Update message to show error
          setChatSessions((prev) => {
            const updated = new Map(prev);
            const session = updated.get(activeSessionId);
            if (session) {
              const messageIndex = session.messages.findIndex((msg) => msg.id === botMessageId);
              if (messageIndex !== -1) {
                const updatedMessages = [...session.messages];
                updatedMessages[messageIndex] = {
                  ...updatedMessages[messageIndex],
                  content: `Error: ${errorMsg}`,
                  isStreaming: false,
                  hasError: true,
                };
                session.messages = updatedMessages;
                updated.set(activeSessionId, session);
              }
            }
            return updated;
          });
          setIsLoading(false);
        };

        // Start streaming
        const result = await apiService.sendMessageStream(
          authToken,
          activeSessionId,
          messageText || '',
          { agentId: session?.agentId, ...(attachments.length > 0 ? { attachments } : {}) },
          onChunk,
          onComplete,
          onError,
        );

        return { success: result.ok };
      } catch (err) {
        const errorMsg = 'Network error sending streaming message';
        setError(errorMsg);
        setIsLoading(false);
        return { success: false, error: errorMsg };
      }
    },
    [activeSessionId, authToken, chatSessions],
  );

  // End a chat session
  const endSession = useCallback(
    async (sessionId) => {
      if (!sessionId || !authToken) {
        return { success: false, error: 'Invalid session or authentication' };
      }

      try {
        const result = await apiService.endChatSession(authToken, sessionId);

        if (result.ok) {
          setChatSessions((prev) => {
            const updated = new Map(prev);
            updated.delete(sessionId);
            return updated;
          });

          // If we're ending the active session, switch to another one
          if (activeSessionId === sessionId) {
            const remainingSessions = Array.from(chatSessions.keys()).filter((id) => id !== sessionId);
            if (remainingSessions.length > 0) {
              setActiveSessionId(remainingSessions[0]);
            } else {
              setActiveSessionId(null);
            }
          }

          return { success: true };
        } else {
          return { success: false, error: result.error?.message || 'Failed to end session' };
        }
      } catch (err) {
        return { success: false, error: 'Network error ending session' };
      }
    },
    [activeSessionId, authToken, chatSessions],
  );

  // Clear messages for a session
  const clearSessionMessages = useCallback((sessionId) => {
    setChatSessions((prev) => {
      const updated = new Map(prev);
      const session = updated.get(sessionId);
      if (session) {
        session.messages = [];
        updated.set(sessionId, session);
      }
      return updated;
    });
  }, []);

  // Get current active session
  const getActiveSession = useCallback(() => {
    return activeSessionId ? chatSessions.get(activeSessionId) : null;
  }, [activeSessionId, chatSessions]);

  // Get all sessions for sidebar
  const getAllSessions = useCallback(() => {
    return Array.from(chatSessions.values()).map((session) => ({
      id: session.id,
      name: session.name,
      agentId: session.agentId,
      lastActivity: session.lastActivity,
      messageCount: session.messages.length - 1, // Subtract welcome message
      isActive: session.id === activeSessionId,
    }));
  }, [chatSessions, activeSessionId]);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  // Clear all chat data (sessions and localStorage)
  const clearAllData = useCallback(() => {
    setChatSessions(new Map());
    setActiveSessionId(null);
    StorageUtils.clearStorage();
    console.log('Cleared all chat sessions and localStorage data');
  }, []);

  // Export sessions to JSON (for backup)
  const exportSessions = useCallback(() => {
    try {
      const sessionData = Array.from(chatSessions.entries());
      const exportData = {
        sessions: sessionData,
        activeSessionId: activeSessionId,
        exportDate: new Date().toISOString(),
        version: '1.0',
      };
      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error('Failed to export sessions:', error);
      return null;
    }
  }, [chatSessions, activeSessionId]);

  // Import sessions from JSON
  const importSessions = useCallback((jsonData) => {
    try {
      const importData = JSON.parse(jsonData);
      if (importData.sessions && Array.isArray(importData.sessions)) {
        const newSessions = new Map(importData.sessions);
        setChatSessions(newSessions);

        // Restore active session if it exists in imported data
        if (importData.activeSessionId && newSessions.has(importData.activeSessionId)) {
          setActiveSessionId(importData.activeSessionId);
        } else {
          setActiveSessionId(null);
        }

        console.log(`Imported ${newSessions.size} chat sessions`);
        return { success: true, sessionCount: newSessions.size };
      } else {
        throw new Error('Invalid import data format');
      }
    } catch (error) {
      console.error('Failed to import sessions:', error);
      return { success: false, error: error.message };
    }
  }, []);

  return {
    // State
    chatSessions,
    activeSessionId,
    isLoading,
    error,
    success,

    // Actions
    startNewSession,
    switchSession,
    sendMessage,
    sendMessageStream,
    endSession,
    clearSessionMessages,

    // Getters
    getActiveSession,
    getAllSessions,
    clearNotifications,

    // Storage management
    clearAllData,
    exportSessions,
    importSessions,
  };
};
